import { makeStyles, Alert, Typography, useClientOptions } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { QuoteContext } from '@paintscout/react-quote'
import { getObjectLabels } from '@paintscout/util/builder'
import { useSnackbar } from 'notistack'
import type { QuoteDocument } from 'paintscout'
import React, { useContext, useState } from 'react'
import { useDealMutations, useQuoteMutations } from 'shared/hooks'
import type { Deal } from '@paintscout/api'

const useStyles = makeStyles((_theme: Theme) => ({
  root: {
    opacity: '1 !important'
  },
  restoreLink: {
    color: 'inherit',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontWeight: 'inherit',
    background: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    marginLeft: '4px',
    font: 'inherit',
    display: 'inline',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    verticalAlign: 'baseline',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 1,
    '&:hover': {
      textDecoration: 'underline'
    },
    '&:focus': {
      outline: 'none',
      textDecoration: 'underline'
    },
    '&:active': {
      opacity: 0.8
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      textDecoration: 'underline',
      pointerEvents: 'none'
    }
  }
}))

export interface RecoveryNoticeProps {
  quote?: QuoteDocument
  onRestoreQuote?: (quote: QuoteDocument) => Promise<void>
  deal?: Deal
  onRestoreDeal?: () => Promise<void>
  itemLabel?: string
  onRestored?: () => void
}

const RecoveryNotice: React.FC<RecoveryNoticeProps> = (props) => {
  const classes = useStyles()
  const quoteContext = useContext(QuoteContext)
  const [loading, setLoading] = useState(false)
  const { options } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const { recoverInvoice } = useQuoteMutations()
  const { saveDeal } = useDealMutations()

  const quote = props.quote || quoteContext?.quote
  const { deal, onRestoreQuote, onRestoreDeal, onRestored, itemLabel } = props

  const objectLabels = quote ? getObjectLabels({ options, invoice: quote.is_invoice }) : null
  const label = itemLabel || (objectLabels ? objectLabels.quote.value.toLowerCase() : 'item')

  async function handleRecover() {
    try {
      setLoading(true)
      if (deal) {
        if (onRestoreDeal) {
          await onRestoreDeal()
        } else {
          await saveDeal({ deal: { ...deal, trashed: false, deleteDate: null } })
        }
        enqueueSnackbar('Successfully restored deal.', { variant: 'success' })
      } else if (quote) {
        if (onRestoreQuote) {
          await onRestoreQuote(quote)
        } else if (quoteContext?.save) {
          if (quote.is_invoice && !!quote.parent_quote) {
            const result = await recoverInvoice({ id: quote._id }).catch(() => null)
            if (result?.data?.recoverInvoice?.quote) {
              // recoverInvoice already sets isRestored: true
              await quoteContext.save(result.data.recoverInvoice.quote)
            }
          } else {
            await quoteContext.save({ ...quote, trashed: false, deleteDate: null, isRestored: true })
          }
        }
        enqueueSnackbar(`Successfully recovered ${objectLabels?.quote.value || 'item'}.`, { variant: 'success' })
      }
      onRestored?.()
    } catch (error) {
      console.log(error)
      enqueueSnackbar(`Unable to recover ${label}.`, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const isTrashed = deal?.trashed || quote?.trashed

  if (!isTrashed) {
    return null
  }

  return (
    <Alert severity="warning" className={classes.root}>
      <Typography component="div" style={{ pointerEvents: 'auto' }}>
        {`This ${label} has been deleted and must be restored before you can interact with it. `}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!loading) {
              handleRecover()
            }
          }}
          className={classes.restoreLink}
          disabled={loading}
          aria-label="Restore this deleted item"
          style={{ pointerEvents: loading ? 'none' : 'auto' }}
        >
          Restore
        </button>
      </Typography>
    </Alert>
  )
}

export default RecoveryNotice
